import { parseArray, parseDate, parseString } from 'utils/parsingHelpers'

import { GeckoEntryBase } from './geckoEntry'
import { RolodexEntryJson } from './rolodexEntryFields'

export enum GeckoFileCategory {
  GECKO_REPORT = 'gecko_report',
  NON_GECKO_REPORT = 'non_gecko_report',
  IMAGE = 'image',
  CONSTRUCTION_DRAWING = 'construction_drawing',
  INSPECTION_WALKDOWN_REPORT = 'inspection_walkdown_report',
  JSA = 'jsa',
  OTHER = 'other',
}

export const GeckoFileCategoryLabels = {
  [GeckoFileCategory.GECKO_REPORT]: 'Gecko Report',
  [GeckoFileCategory.NON_GECKO_REPORT]: 'Non-Gecko Report',
  [GeckoFileCategory.IMAGE]: 'Image',
  [GeckoFileCategory.CONSTRUCTION_DRAWING]: 'Construction Drawing',
  [GeckoFileCategory.OTHER]: 'Other',
  [GeckoFileCategory.INSPECTION_WALKDOWN_REPORT]: 'Inspection Walkdown Report',
  [GeckoFileCategory.JSA]: 'Job Safety Analysis',
}

export const GeckoAllowableFileTypes = {
  [GeckoFileCategory.IMAGE]: 'image/*',
  [GeckoFileCategory.GECKO_REPORT]: 'application/pdf',
  [GeckoFileCategory.NON_GECKO_REPORT]: 'application/pdf, .doc, .docx, ',
  [GeckoFileCategory.CONSTRUCTION_DRAWING]: 'application/pdf',
  [GeckoFileCategory.INSPECTION_WALKDOWN_REPORT]: 'application/pdf',
  [GeckoFileCategory.JSA]: 'application/pdf',
  [GeckoFileCategory.OTHER]:
    'video/mp4, video/*, application/*, text/*, audio/*, !application/pdf',
}

// https://developer.mozilla.org/en-US/docs/Web/Media/Formats/Image_types
export const IMAGE_TYPES = [
  'png',
  'jpg',
  'jpeg',
  'tiff',
  'jfif',
  'pjpeg',
  'pjp',
  'tif',
  'bmp',
  'gif',
  'svg',
  'webp',
]

export interface GeckoReportJSON {
  visible_name: string
  file_caption?: string
  file_type: string | false
  upload_date: string
  size: number
  units: string[]
  components: string[]
  inspections: string[]
  jobs: string[]
  file_category: GeckoFileCategory
  viz_insp_field_responses?: string[]
  visual_inspections?: string[]
}

/**
 * An entry which contains metadata for a "report" file. In the
 * context could mean a PDF, PNG, JPEG, or any number of other formats.
 */
export class GeckoReport extends GeckoEntryBase<
  {
    visibleName: string
    fileType: string
    jobs: ReadonlyArray<string> | undefined
    inspections: ReadonlyArray<string> | undefined
    units: ReadonlyArray<string> | undefined
    components: ReadonlyArray<string> | undefined
    upload_date: Date | undefined
    file_category: GeckoFileCategory | undefined
    file_caption: string | undefined
    vizInspFieldResponses: ReadonlyArray<string> | undefined
    visualInspections: ReadonlyArray<string> | undefined
  },
  {
    organization: string
    site: string
    job?: string
    inspection?: string
    unit?: string
    component?: string
  }
> {
  //----------------------------------------------------------------------------

  /**
   * A simple constructor, private so that a more descriptive factory method
   * (which can return undefined) is used instead
   * @param json The raw JSON to create an instance from
   * @private
   */
  private constructor(json: RolodexEntryJson) {
    super(
      json,
      {
        visibleName: parseString(json.data.visible_name) ?? '',
        fileType: parseString(json.data.file_type) ?? '',
        jobs: parseArray<string>(json.data.jobs),
        inspections: parseArray<string>(json.data.inspections),
        units: parseArray<string>(json.data.units),
        components: parseArray<string>(json.data.components),
        upload_date: parseDate(json.data.upload_date),
        file_category: IMAGE_TYPES.includes(
          parseString(json.data.file_type) ?? ''
        )
          ? GeckoFileCategory.IMAGE
          : ((parseString(json.data.file_category) as GeckoFileCategory) ??
            GeckoFileCategory.OTHER),
        file_caption: parseString(json.data.file_caption),
        vizInspFieldResponses: parseArray<string>(
          json.data.viz_insp_field_responses
        ),
        visualInspections: parseArray<string>(json.data.visual_inspections),
      },
      {
        organization: parseString(json.tags.organization) ?? '',
        site: parseString(json.tags.site) ?? '',
        inspection: parseString(json.tags.unit),
        job: parseString(json.tags.unit),
        component: parseString(json.tags.unit),
        unit: parseString(json.tags.unit),
      },
      {}
    )
  }

  //----------------------------------------------------------------------------

  /**
   * A simple factory method which creates a report from raw JSON
   * @param json The raw JSON to create an instance from
   * @constructor
   */
  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoReport(json)
  }
}
